import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import { KaraokeTrackManager } from '@src/editor/lib/KaraokeTrackManager';

import { AbstractCommand } from '../AbstractCommand';
import { TimelineMode } from '../TimelineMode';

import type { TranscriptTrackManager } from '@src/editor/lib/TranscriptTrackManager';
import type { Track } from '@src/editor/models';
import type { HasClipTranscriptSegment } from '@src/editor/models/elements/HasClipTranscriptSegment';
import type { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
  ElementResizeResult,
} from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

type OperationType =
  | 'merge'
  | 'extendPrev'
  | 'extendNext'
  | 'insert'
  | 'restorePunctuation'
  | 'none';

/**
 * TranscriptSimpleRestoreCommand – restores a contiguous range of transcript
 * words (specified by their original `initial_index` values).  It looks at
 * the neighbouring VideoElements (if any) to determine the minimal timeline
 * operation required:
 *   • merge  – when the range lies exactly between two adjacent clips
 *   • extendPrev – when the range sits immediately after the previous clip
 *   • extendNext – when the range sits immediately before the next clip
 *   • insert – when no adjacent clip touches the range (paste anywhere)
 *
 * The command stores a snapshot of the entire transcript track before
 * execution, allowing undo to simply restore that snapshot.
 */
export class TranscriptRestoreCommand extends AbstractCommand {
  getName(): string {
    return 'TranscriptRestoreCommand';
  }

  private readonly fromClipSegmentId: string;
  private readonly toClipSegmentId: string;

  private readonly fromIndex: number; // 0 based index
  private readonly toIndex: number; // inclusive

  private transcriptTrack: TranscriptTrack | null = null;
  private readonly transcriptTrackManager: TranscriptTrackManager;

  private transcriptTrackElements: readonly HasClipTranscriptSegment[] = [];

  // private readonly navigator: TranscriptNavigator;

  // (removed unused undo snapshot & insert command state)

  private timelineSegmentAdded: ElementResizeResult | null = null;

  constructor(
    editor: VideoEditor,
    fromClipSegmentId: string,
    fromIndex: number,
    toClipSegmentId: string,
    toIndex: number,
  ) {
    super(editor);
    this.fromClipSegmentId = fromClipSegmentId;
    this.toClipSegmentId = toClipSegmentId;
    this.fromIndex = fromIndex;
    this.toIndex = toIndex;

    // this.navigator = new TranscriptNavigator(this.transcript, editor);

    this.transcriptTrackManager = this.editor.getTranscriptTrackManager();

    this.transcriptTrack = editor.getVideoTranscriptTrack()!;
    this.transcriptTrackElements =
      this.transcriptTrack.transcriptSegmentElements;
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.transcriptTrack;
    if (!track) {
      return false;
    }

    if (this.fromClipSegmentId !== this.toClipSegmentId) {
      console.error(
        'TranscriptSimpleRestoreCommand: start and end elements should belong to the same redline segment - fromClipSegmentId and toClipSegmentId must be the same',
      );
      return false;
    }

    const { prev, next } = this.lookupNeighbours(this.fromClipSegmentId);
    const op = this.determineOperation(prev, next);

    switch (op) {
      case 'merge':
        if (!prev || !next) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing prev or next for merge operation',
          );
          break;
        }
        this.applyMerge(prev, next);
        break;
      case 'extendPrev':
        if (!prev) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing prev for extendPrev operation',
          );
          break;
        }
        this.extendPrev(prev);
        break;
      case 'extendNext':
        if (!next) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing next for extendNext operation',
          );
          break;
        }
        this.extendNext(next);
        break;
      case 'insert':
        this.performInsert(prev, next);
        break;
      case 'restorePunctuation':
        this.applyRestorePunctuation();
        break;
      case 'none':
        return false;
    }

    // Recalculate timeline and mark dirty
    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_restore');

    // Reconcile karaoke track after restore operation
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (karaokeTrack) {
      const karaokeTrackManager = new KaraokeTrackManager(this.editor);
      karaokeTrackManager.reconcileKaraokeTrack();
    }

    return true;
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Simple restore words (${this.fromClipSegmentId}-#${this.fromIndex}-${this.toClipSegmentId}-#${this.toIndex})`;
  }

  getTimelineSegmentAdded() {
    return this.timelineSegmentAdded;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private getTranscriptTrack(): Track | null {
    if (this.transcriptTrack) return this.transcriptTrack;
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) return null;
    this.transcriptTrack = track;
    return track;
  }

  private lookupNeighbours(clipSegmentId: string): {
    prev: VideoTranscriptElement | null;
    next: VideoTranscriptElement | null;
  } {
    // Find adjacent timeline elements in a single call closest to
    // the fromClipSegmentId and toClipSegmentId
    const trackElementIndex = this.transcriptTrackElements.findIndex(
      el => el.getClipTranscriptSegment().id === clipSegmentId,
    );

    const prevVideoTranscriptElement = this.transcriptTrackElements
      .filter(
        (el, index) =>
          index < trackElementIndex && el instanceof VideoTranscriptElement,
      )
      .pop() as VideoTranscriptElement | null;

    const nextVideoTranscriptElement = this.transcriptTrackElements.find(
      (el, index) =>
        index > trackElementIndex && el instanceof VideoTranscriptElement,
    ) as VideoTranscriptElement | null;

    return {
      prev: prevVideoTranscriptElement,
      next: nextVideoTranscriptElement,
    };
  }

  private determineOperation(
    prev: VideoTranscriptElement | null,
    next: VideoTranscriptElement | null,
  ): OperationType {
    // Check if this is a punctuation-only restoration in a VideoTranscriptElement
    const segment = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
    );

    // Only allow punctuation restoration in VideoTranscriptElement (not RemovedTranscriptClip)
    if (segment && segment instanceof VideoTranscriptElement) {
      const clipSegment = segment.getClipTranscriptSegment();
      const wordsWithPunctuation = clipSegment.getWordsWithPunctuation();

      // Check if range contains only punctuation tokens
      const tokensInRange = wordsWithPunctuation.filter(
        (_, idx) => idx >= this.fromIndex && idx <= this.toIndex,
      );
      const isPunctuationOnly = tokensInRange.every(
        token => token.type === 'punct',
      );

      if (isPunctuationOnly && tokensInRange.length > 0) {
        return 'restorePunctuation';
      }
    }

    const prevContiguous =
      prev &&
      !prev.getClipTranscriptSegment().hasWordsAdded &&
      this.restoresStartOfRange()
        ? true
        : false;
    const nextContiguous =
      next &&
      !next.getClipTranscriptSegment().hasWordsAdded &&
      this.restoresEndOfRange()
        ? true
        : false;

    const restoresSomeWords = this.getSegmentWordsToRestore().length > 0;

    if (!restoresSomeWords) return 'none';
    if (prevContiguous && nextContiguous) return 'merge';
    if (prevContiguous) return 'extendPrev';
    if (nextContiguous) return 'extendNext';
    if (!prev && !next) return 'insert';
    // Could insert between prev and next if gap, treat as insert
    return 'insert';
  }

  /**
   * Checks if restored part includes the first word of the segment
   * meaning there are no other words after the previous video element
   */
  private restoresStartOfRange(): boolean {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
      ?.getClipTranscriptSegment();

    const wordsWithPunctuation =
      trackElementSegment?.getWordsWithPunctuation() || [];

    // no text before this.fromIndex
    return (
      wordsWithPunctuation.findIndex(
        (el, idx) => el.type === 'text' && idx < this.fromIndex,
      ) === -1
    );
  }

  /**
   * Checks if restored part includes the last word of the segment
   * meaning there are no other words before the next video element
   */
  private restoresEndOfRange(): boolean {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.toClipSegmentId)
      ?.getClipTranscriptSegment();

    const wordsWithPunctuation =
      trackElementSegment?.getWordsWithPunctuation() || [];

    // no text after this.toIndex
    return (
      wordsWithPunctuation.findIndex(
        (el, idx) => el.type === 'text' && idx > this.toIndex,
      ) === -1
    );
  }

  private applyMerge(
    prev: VideoTranscriptElement,
    next: VideoTranscriptElement,
  ): void {
    // Merge and rely on TrackManager's ripple handling
    const track = this.transcriptTrack;
    if (!track) return;

    // store original states in snapshot already, so just merge
    this.transcriptTrackManager.bridgeAndMergeTranscriptSegmentElements(
      [prev, next],
      track,
    );
  }

  private extendPrev(prev: VideoTranscriptElement): void {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.toClipSegmentId)
      ?.getClipTranscriptSegment();
    const lastWordToRestore = trackElementSegment
      ?.getWordsWithPunctuation()
      .filter(
        (el, idx): el is ClipTranscriptTextToken =>
          el.type === 'text' && idx <= this.toIndex,
      )
      .pop();

    if (!lastWordToRestore) return;

    // Calculate new duration based on the difference between the element's trim_start and the last word's media_end_ts
    // This gives us the duration from the clip start to the end of the last word in original media time
    const newDuration = lastWordToRestore.media_end_ts - (prev.trim_start ?? 0);

    // Calculate the corresponding end time on the timeline
    const newEndTime = prev.time + newDuration;

    this.transcriptTrackManager.trimElementEnd(
      prev,
      newEndTime,
      TimelineMode.RIPPLE,
      false,
    );
  }

  private extendNext(next: VideoTranscriptElement): void {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
      ?.getClipTranscriptSegment();
    const firstWordToRestore = trackElementSegment
      ?.getWordsWithPunctuation()
      .find(
        (el, idx): el is ClipTranscriptTextToken =>
          el.type === 'text' && idx >= this.fromIndex,
      );

    if (!firstWordToRestore) return;

    // Calculate new start time based on the first word to restore
    const newTrimStart = firstWordToRestore.media_ts;

    // Use TranscriptTrackManager to adjust the element start
    this.transcriptTrackManager.adjustTranscriptElementMediaStart(
      next,
      newTrimStart,
      TimelineMode.RIPPLE,
      false,
    );
  }

  private performInsert(
    prev: VideoTranscriptElement | null,
    next: VideoTranscriptElement | null,
  ): void {
    const words = this.getSegmentWordsToRestore();
    if (!words.length) {
      console.error('Insert: No words to insert!');
      return;
    }

    // Calculate insertion parameters
    const duration = this.estimateDuration();
    const trimStart = words[0].media_ts;
    let time = 0;

    if (prev) {
      time = prev.end_time;
    } else if (next) {
      time = Math.max(0, next.time - duration);
    }

    // Create transcript segment for restoration
    const restoredTranscriptSegment = new ClipTranscriptSegment(
      this.getSegmentTokensToRestore().map(el =>
        el.type === 'text'
          ? {
              ...el,
              relative_ts: el.media_ts - trimStart,
              relative_end_ts: el.media_end_ts - trimStart,
            }
          : { ...el },
      ),
      trimStart,
      trimStart + duration,
    );

    const transcriptSegmentPosition = this.transcriptTrackElements.findIndex(
      el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
    );

    if (transcriptSegmentPosition < 0) {
      console.error('Insert: Could not find transcript segment position');
      return;
    }

    // Use TranscriptTrackManager to handle the insertion
    const sourceElement = prev || next;
    this.transcriptTrackManager.insertTranscriptSegmentAsVideoElement(
      restoredTranscriptSegment,
      sourceElement,
      time,
      this.transcriptTrack!,
      transcriptSegmentPosition,
      this.fromIndex,
      this.toIndex,
    );
  }

  private getSegmentWordsToRestore(): ClipTranscriptTextToken[] {
    return (
      this.getSegmentTokensToRestore().filter(
        (el): el is ClipTranscriptTextToken => el.type === 'text',
      ) || []
    );
  }

  private getSegmentTokensToRestore(): ClipTranscriptToken[] {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
      ?.getClipTranscriptSegment();

    return (
      trackElementSegment
        ?.getWordsWithPunctuation()
        .slice(this.fromIndex, this.toIndex + 1) || []
    );
  }

  private estimateDuration(): number {
    const words = this.getSegmentWordsToRestore();
    if (!words.length) return 0;
    return words[words.length - 1].media_end_ts - words[0].media_ts;
  }

  private applyRestorePunctuation(): void {
    // Find the VideoTranscriptElement that contains the punctuation to restore
    const element = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
    );

    if (!element || !(element instanceof VideoTranscriptElement)) {
      console.error(
        'RestorePunctuation: Element not found or not a VideoTranscriptElement',
      );
      return;
    }

    // Use TranscriptTrackManager to restore punctuation
    const success = this.transcriptTrackManager.restorePunctuation(
      element,
      this.fromIndex,
      this.toIndex,
    );

    if (!success) {
      console.warn('RestorePunctuation: Failed to restore punctuation');
    }
  }
}
